/**
 * Layout for the join funnel — cream background, GET STARTED eyebrow,
 * 3-segment progress bar, sticky footer with back arrow + light-blue CTA.
 *
 * Reuses the StickyFooterBar pattern from quizletv2 but provides its own
 * header since the funnel has a fixed 3 steps and doesn't need the question/
 * category gymnastics.
 *
 * Wrapped in .dashboard-reset so Tailwind border/background utilities aren't
 * overridden by the app-wide Chakra/Radix button reset (which sets
 * `button { background: transparent; padding: 0 }` at higher cascade priority
 * than Tailwind's utility layer).
 */

import BackArrow from '@/app/components/questionnaireV2/icons/back-arrow';
import type { ReactNode } from 'react';
import '../dashboard/dashboard-reset.css';

interface JoinFunnelLayoutProps {
  /** 1, 2, or 3 — which step we're on. */
  step: 1 | 2 | 3;
  /** Page content (header, fields, value-prop card). */
  children: ReactNode;
  /** Back arrow click handler. Hide the back button by passing undefined. */
  onBack?: () => void;
  /** CTA click handler. */
  onContinue: () => void;
  /** CTA disabled (e.g. invalid form, under-18). */
  isContinueDisabled?: boolean;
  /** True while a network request triggered by Continue is in flight. */
  isContinuePending?: boolean;
  /** CTA label override. Defaults vary by step (Continue / Continue to Checkout). */
  continueLabel?: string;
}

const TOTAL_STEPS = 3;

function ProgressBar({ step }: { step: 1 | 2 | 3 }) {
  return (
    <div className="flex items-center gap-1.5" aria-hidden="true">
      {Array.from({ length: TOTAL_STEPS }, (_, i) => {
        const isFilled = i < step;
        return (
          <div
            key={i}
            className="h-1 flex-1 rounded-full transition-colors duration-300"
            style={{
              backgroundColor: isFilled
                ? 'var(--color-catch-light-blue)'
                : 'rgba(15, 36, 52, 0.12)',
            }}
          />
        );
      })}
    </div>
  );
}

export function JoinFunnelLayout({
  step,
  children,
  onBack,
  onContinue,
  isContinueDisabled = false,
  isContinuePending = false,
  continueLabel,
}: JoinFunnelLayoutProps) {
  const defaultLabel = step === 3 ? 'Continue to Checkout' : 'Continue';
  const label = continueLabel ?? defaultLabel;

  return (
    <div className="dashboard-reset flex min-h-screen flex-col bg-catch-tan">
      <header className="flex w-full flex-col px-6 pt-safe-offset-4 pb-3">
        <div className="mx-auto flex w-full max-w-lg flex-col">
          <div className="flex w-full items-center justify-between py-2">
            <span className="text-xs font-bold uppercase tracking-widest text-catch-dark-blue-90">
              Get Started
            </span>
            <span className="text-xs font-medium tabular-nums text-gray-500">
              {step}/{TOTAL_STEPS}
            </span>
          </div>
          <ProgressBar step={step} />
        </div>
      </header>

      <main className="flex w-full flex-1 flex-col px-6 pb-32">
        <div className="mx-auto flex w-full max-w-lg flex-col gap-8">{children}</div>
      </main>

      <div className="sticky bottom-0 z-20 flex w-full shrink-0 flex-col items-center border-t border-solid border-[#E3DCCE] bg-catch-tan px-6 pt-3 pb-safe-offset-6">
        <div className="mx-auto flex w-full max-w-lg items-stretch gap-2">
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              className="inline-flex h-[52px] w-[52px] shrink-0 items-center justify-center rounded-full border-1 border-solid border-gray-200 bg-white text-gray-600 transition-colors hover:border-gray-300"
              aria-label="Go back"
            >
              <BackArrow />
            </button>
          )}
          <button
            type="button"
            onClick={onContinue}
            disabled={isContinueDisabled || isContinuePending}
            className="flex min-w-0 flex-1 items-center justify-center gap-2 rounded-full bg-catch-light-blue p-[16px_24px] text-base font-semibold text-catch-dark-blue-90 transition-all hover:brightness-95 focus:outline-none focus:ring-2 focus:ring-catch-light-blue focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isContinuePending ? (
              <span className="inline-flex items-center gap-2">
                <svg className="h-5 w-5 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Saving…
              </span>
            ) : (
              <>
                <span>{label}</span>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="12 5 19 12 12 19" />
                </svg>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
